import React from 'react';
import styled from 'styled-components';
import { Assignment } from '../types/alarm';

interface AssignmentBlockProps {
  assignment: Assignment;
  onCopy: () => void;
  onUpdated: () => void;
  onDelete: () => void;
  onCheck: () => void; // 과제 체크 모달 열기 함수 추가
  categoryName?: string;
  categoryColor?: string;
  onMenuToggle: (assignmentId: string) => void;
  isMenuOpen: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  onSelectToggle?: () => void;
}

const AssignmentBlock: React.FC<AssignmentBlockProps> = ({ assignment, onCopy, onUpdated, onDelete, onCheck, categoryName, categoryColor, onMenuToggle, isMenuOpen, selectionMode = false, isSelected = false, onSelectToggle }) => {
  const due = new Date(assignment.dueDate);
  const weekday = ['일','월','화','수','목','금','토'][due.getDay()];

  // pendingCount: 미확인+제출확인 합(목록 로직과 동일). submittedCount만 남은 경우에도 완료로 보이면 안 됨
  const isAssignmentCompleted =
    assignment.pendingCount !== undefined &&
    assignment.pendingCount === 0 &&
    (assignment.submittedCount === undefined || assignment.submittedCount === 0);

  const handleClick = () => {
    if (selectionMode) {
      onSelectToggle?.();
      return;
    }
    onCheck();
  };

  return (
    <Block 
      onClick={handleClick} 
      role="button" 
      tabIndex={0} 
      $isCompleted={isAssignmentCompleted}
      $selectionMode={selectionMode}
      $isSelected={isSelected}
      data-assignment-id={assignment.id}
    >
      {selectionMode && (
        <SelectionIndicator $checked={isSelected} aria-label={isSelected ? '선택됨' : '선택 안 됨'}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </SelectionIndicator>
      )}
      {/* 1행: 버튼들과 과제 제목 */}
      <TopRow>
        <Left>
          <CategoryBadge 
            color={categoryColor || 'rgba(162, 162, 162, 1)'} 
            $isCompleted={isAssignmentCompleted}
          >
            {categoryName || '기본'}
          </CategoryBadge>
        </Left>
        <TitleCenter $isCompleted={isAssignmentCompleted}>{assignment.title}</TitleCenter>
        <Right>
          {/* 햄버거 메뉴 - 항상 표시 */}
          <HamburgerButton
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle(assignment.id);
            }}
            aria-label="메뉴"
            className="hamburger-button"
            $isCompleted={isAssignmentCompleted}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="1" fill="currentColor"/>
              <circle cx="19" cy="12" r="1" fill="currentColor"/>
              <circle cx="5" cy="12" r="1" fill="currentColor"/>
            </svg>
          </HamburgerButton>
        </Right>
      </TopRow>
      
      {/* 2행: 마감일 정보 */}
      <DueDate $isCompleted={isAssignmentCompleted}>
        <DueDateText>
        {`${due.getMonth()+1}/${due.getDate()} (${weekday}) • 기본포인트 ${assignment.basePoints}`}
          {(assignment.rewardEnabled && assignment.rewardCoins && assignment.rewardCoins >= 1) || 
           (assignment.rewardItemCardIds && assignment.rewardItemCardIds.length > 0) ? (
            <RewardInfo>
              {assignment.rewardEnabled && assignment.rewardCoins && assignment.rewardCoins >= 1 && (
                <RewardItem>
                  <CoinIcon>
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="10" cy="10" r="8" fill="#FFD700" stroke="#FFA500" strokeWidth="1.5"/>
                      <circle cx="10" cy="10" r="5" fill="#FFA500" opacity="0.3"/>
                      <text x="10" y="13" fontSize="8" fontWeight="bold" fill="#FFA500" textAnchor="middle">₩</text>
                    </svg>
                  </CoinIcon>
                  <RewardText>{assignment.rewardCoins}</RewardText>
                </RewardItem>
              )}
              {assignment.rewardItemCardIds && assignment.rewardItemCardIds.length > 0 && (
                <RewardItem>
                  <ItemCardIcon>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                      <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                      <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                    </svg>
                  </ItemCardIcon>
                  <RewardText>{assignment.rewardItemCardIds.length}</RewardText>
                </RewardItem>
              )}
            </RewardInfo>
          ) : null}
        </DueDateText>
      </DueDate>
      
      {/* 3행: 미확인/과제완료/제출확인 상태 표시 */}
      {assignment.pendingCount !== undefined && (
        <StatusRow $isCompleted={isAssignmentCompleted}>
          {assignment.submittedCount !== undefined && assignment.submittedCount > 0 && (
            <SubmittedInfo>
              제출 확인: {assignment.submittedCount}명
            </SubmittedInfo>
          )}
          <PendingInfo $hasPending={!isAssignmentCompleted}>
            {isAssignmentCompleted
              ? '과제 완료'
              : assignment.pendingCount !== undefined && assignment.pendingCount > 0
                ? `미확인: ${assignment.pendingCount}/${assignment.assignedStudentIds?.length || 0}명`
                : (assignment.submittedCount ?? 0) > 0
                  ? `제출 확인 처리 필요 (${assignment.submittedCount}명)`
                  : '미완료'}
          </PendingInfo>
        </StatusRow>
      )}
    </Block>
  );
};

export default AssignmentBlock;

const Block = styled.div<{ $isCompleted?: boolean; $selectionMode?: boolean; $isSelected?: boolean }>`
  background: var(--color-surface);
  position: relative;
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 12px;
  padding: 6px 10px;
  margin: 0;
  box-shadow: ${props => props.$isCompleted ? '0 4px 12px rgba(0, 0, 0, 0.1)' : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  transition: all 0.3s ease;
  cursor: pointer;
  user-select: none;
  opacity: ${props => {
    // 선택되었을 때는 항상 선명하게 (opacity: 1)
    if (props.$isSelected) return 1;
    // 선택되지 않았을 때만 완료 여부에 따라 조정
    return props.$isCompleted ? 0.7 : 1;
  }};
  
  /* 다크 모드에서 선택된 블럭의 테두리를 흰색으로 */
  :root:not([data-theme='light']) & {
    border-color: ${props => props.$isSelected ? '#ffffff' : 'var(--color-border)'};
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.$isCompleted 
      ? '0 4px 12px rgba(0, 0, 0, 0.1)' 
      : '0 8px 24px rgba(0, 0, 0, 0.15)'
    };
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
  }
`;

const SelectionIndicator = styled.div<{ $checked: boolean }>`
  position: absolute;
  top: -8px;
  left: -8px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => props.$checked ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  background: ${props => props.$checked ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$checked ? '#ffffff' : 'var(--color-text-gray)'};
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
  opacity: 1; /* 항상 선명하게 표시 */
  z-index: 10; /* 다른 요소 위에 표시 */
  
  /* 다크 모드에서 선택된 원형 v 도형: 흰색 원형에 진한 회색 v */
  :root:not([data-theme='light']) & {
    border-color: ${props => props.$checked ? '#ffffff' : 'var(--color-border)'};
    background: ${props => props.$checked ? '#ffffff' : 'var(--color-surface)'};
    color: ${props => props.$checked ? '#333333' : 'var(--color-text-gray)'};
  }
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 40px;
  align-items: center;
  margin-bottom: 0;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const TitleCenter = styled.div<{ $isCompleted?: boolean }>`
  text-align: center;
  font-weight: 800;
  font-size: 1.2rem;
  color: ${props => props.$isCompleted ? '#b0b0b0' : 'var(--color-text-primary)'};
  opacity: ${props => props.$isCompleted ? 0.7 : 1};
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  position: relative;
`;

const DueDate = styled.div<{ $isCompleted?: boolean }>`
  text-align: center;
  color: ${props => props.$isCompleted ? '#b0b0b0' : 'var(--color-text-gray)'};
  margin-top: 1px;
  opacity: ${props => props.$isCompleted ? 0.6 : 1};
`;

const DueDateText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RewardInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
`;

const RewardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const CoinIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const ItemCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const RewardText = styled.span`
  font-size: inherit;
  font-weight: 600;
  color: inherit;
`;

const StatusRow = styled.div<{ $isCompleted?: boolean }>`
  text-align: center;
  margin-top: 2px;
  opacity: ${props => props.$isCompleted ? 0.8 : 1};
`;

const CategoryBadge = styled.span<{ color?: string; $isCompleted?: boolean }>`
  background-color: ${props => props.color || 'var(--color-border)'};
  color: ${props => props.color ? '#fff' : '#6c757d'};
  padding: 2px 5px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${props => props.$isCompleted ? 0.3 : 1};
`;

const PendingInfo = styled.span<{ $hasPending: boolean }>`
  color: ${props => props.$hasPending ? 'var(--status-pending)' : 'var(--color-accent-strong)'};
  font-weight: ${props => props.$hasPending ? '600' : '500'};
  font-size: 0.9rem;
  opacity: ${props => props.$hasPending ? 0.8 : 0.6};

  /* 다크 모드: 과제 완료 시 흰색으로 표시 (배경과 구분) */
  :root:not([data-theme='light']) & {
    ${props => !props.$hasPending ? 'color: #ffffff; opacity: 0.95;' : ''}
  }
`;

const SubmittedInfo = styled.span`
  background: #000000;
  color: #ffffff;
  font-weight: 500;
  font-size: 0.8rem;
  padding: 2px 8px;
  border-radius: 20px;
  margin-right: 12px;
  display: inline-block;
  line-height: 1.2;
`;

// 햄버거 메뉴 버튼 - 항상 표시
const HamburgerButton = styled.button<{ $isCompleted?: boolean }>`
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  border: none;
  background: transparent;
  color: ${props => props.$isCompleted ? '#b0b0b0' : 'var(--color-text-gray)'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$isCompleted ? 0.6 : 1};
  padding: 0;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

